import { Component } from '@theme/component';

/**
 * Previous / next arrows for a mobile scroll-snap carousel built from group or section content.
 * Place it as a sibling of the scrolling container; the scroller is the first descendant of the parent
 * that is currently scrollable on the x axis.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 *
 * @extends {Component<Refs>}
 */
export class MobileCarouselArrows extends Component {
  requiredRefs = ['previous', 'next'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLElement | null} */
  #scroller = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    window.addEventListener('resize', this.#update, { signal });
    this.#bindScroller();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#scroller = null;
  }

  /** @returns {HTMLElement | null} */
  #findScroller() {
    const parent = this.parentElement;
    if (!parent) return null;

    const candidates = parent.querySelectorAll(
      '.section-content-wrapper--mobile-carousel, .section-content-wrapper--mobile-carousel > .group-block:only-child > .group-block-content, .group-block-content--mobile-carousel'
    );

    for (const candidate of candidates) {
      if (candidate instanceof HTMLElement && getComputedStyle(candidate).overflowX === 'auto') return candidate;
    }

    return null;
  }

  #bindScroller() {
    this.#scroller = this.#findScroller();
    if (!this.#scroller || !this.#abortController) return;

    this.#scroller.addEventListener('scroll', this.#update, { passive: true, signal: this.#abortController.signal });
    this.#update();
  }

  /**
   * @param {1 | -1} direction
   */
  #scrollByItem(direction) {
    const scroller = this.#scroller ?? this.#findScroller();
    const item = scroller?.firstElementChild;
    if (!scroller || !(item instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    scroller.scrollBy({ left: direction * (item.offsetWidth + gap) * (this.#isRtl(scroller) ? -1 : 1), behavior });
  }

  /**
   * @param {HTMLElement} element
   */
  #isRtl(element) {
    return getComputedStyle(element).direction === 'rtl';
  }

  #update = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    const max = scroller.scrollWidth - scroller.clientWidth;
    const position = Math.abs(scroller.scrollLeft);

    this.refs.previous.disabled = position <= 1;
    this.refs.next.disabled = position >= max - 1;
  };

  /** Handler for `on:click` on the previous button. */
  previous() {
    this.#scrollByItem(-1);
  }

  /** Handler for `on:click` on the next button. */
  next() {
    this.#scrollByItem(1);
  }
}

if (!customElements.get('mobile-carousel-arrows')) {
  customElements.define('mobile-carousel-arrows', MobileCarouselArrows);
}
